import { useRef, useState } from 'react';

import snakecaseKeys from 'snakecase-keys';

import { deleteRequest, get, patch, post } from 'helpers/api';
import { isInlineToolCall } from '../tools/inline';
import { useTurnSubscription } from './use_turn_subscription';

import type { RecordChange } from 'helpers/record_changed';
import type { ToolCall } from '../tools/types';
import type {
  ActionVerdict, Agent, AssistantAction, AssistantActionStatus, InputMode, Message, PageContext, Paths, StreamFrame,
} from '../types';

export function getPageContext(): PageContext {
  return { path: window.location.pathname };
}

// A resumed message carries the same undifferentiated tool calls a live turn streams, so the
// inline ones are picked out here rather than by the backend.
function toResumedMessage({ toolCalls, ...message }: Message & { toolCalls?: ToolCall[] }): Message {
  return { ...message, inlineToolCalls: toolCalls?.filter(isInlineToolCall) };
}

// A turn with no reply still arrives when its tool calls recorded something for the client, hung off
// the message that asked for them even though it says nothing. Navigate and Support render outside
// the transcript, which leaves nothing behind once the inline filter above has run.
function isRenderable(message: Message): boolean {
  return !!(message.content || message.inlineToolCalls?.length || message.actions?.length);
}

interface Options {
  // Returning null opts a surface out entirely, for one with no page worth reporting
  pageContext: () => PageContext | null;
}

export function useAssistant(agents: Agent[] | null | undefined, paths: Paths, { pageContext }: Options) {
  const { loading, run, stop } = useTurnSubscription();

  const [agent, setAgent] = useState((agents && agents[0]) || null);
  const [model, setModel] = useState(agent?.models.find(({ id }) => id === agent.model) || null);
  const [chatId, setChatId] = useState<number | null>(null);
  const chatIdRef = useRef(chatId);
  chatIdRef.current = chatId;
  const pendingAskRef = useRef<Promise<void>>(Promise.resolve());
  // Sending context only when it changes means the backend never has to de-duplicate it
  const sentPageContext = useRef<string | null>(null);

  async function loadChat(): Promise<{ chatId: number; messages: Message[] } | null> {
    const response = await get(paths.chat);
    if (!response?.chatId) return null;

    const messages = (response.messages ?? []).map(toResumedMessage).filter(isRenderable);
    return { chatId: response.chatId, messages };
  }

  async function resetChat() {
    const dismissedChatId = chatId;
    setChatId(null);
    sentPageContext.current = null;

    try {
      await deleteRequest(paths.chat, dismissedChatId != null ? { params: { chatId: dismissedChatId } } : {});
    } catch {
      // Dismissal is best-effort; on failure the old chat is offered for resume again.
    }
  }

  async function ask({ attachments, inputMode, prompt, onFrame }: {
    attachments?: File[],
    inputMode: InputMode,
    prompt: string,
    onFrame: (frame: StreamFrame) => void
  }) {
    function buildAskFormData(requestId: string) {
      const form = new FormData();

      form.append('prompt', prompt);
      form.append('request_id', requestId);
      form.append('input_mode', inputMode);
      attachments?.forEach(attachment => form.append('attachments[]', attachment));

      const context = pageContext();
      const serializedPageContext = context && JSON.stringify(context);

      if (context && serializedPageContext !== sentPageContext.current) {
        sentPageContext.current = serializedPageContext;
        Object.entries(snakecaseKeys(context)).forEach(([key, value]) => {
          if (value != null) form.append(`page_context[${key}]`, String(value));
        });
      }

      if (agent?.name) {
        form.append('agent', agent.name);
      }

      if (chatIdRef.current != null) {
        form.append('chat_id', String(chatIdRef.current));
      }

      if (model?.id) {
        form.append('model', model.id);
      }

      return form;
    }

    return run(async (requestId) => {
      const previous = pendingAskRef.current;
      const current = (async () => {
        await previous.catch(() => {});
        const response = await post(paths.ask, buildAskFormData(requestId));
        chatIdRef.current = response.chatId;
        setChatId(response.chatId);
      })();

      pendingAskRef.current = current;
      await current;
      return true;
    }, onFrame);
  }

  async function resolveAction({ action, verdict, onResolved, onFrame }: {
    action: AssistantAction,
    verdict: ActionVerdict,
    onResolved: (status: AssistantActionStatus, clientChanges?: RecordChange[]) => void,
    onFrame: (frame: StreamFrame) => void
  }) {
    const path = verdict === 'apply' ? action.applyPath : action.rejectPath;

    return run(async (requestId) => {
      const response = await post(path, { requestId, agent: agent?.name });
      onResolved(response.status, response.clientChanges);
      return response.resumed;
    }, onFrame);
  }

  async function resolveQuestions({ answerPath, answers, dismissed, onResolved, onFrame }: {
    answerPath: string,
    answers?: (string | null)[],
    dismissed?: boolean,
    onResolved: () => void,
    onFrame: (frame: StreamFrame) => void
  }) {
    return run(async (requestId) => {
      // A skipped answer travels as a blank, never a null: Rails compacts nulls out of an array
      // param, which would shift every answer after a skipped one onto the wrong question.
      const response = await patch(answerPath, {
        agent: agent?.name,
        answers: answers?.map(answer => answer ?? ''),
        dismissed,
        requestId,
      });
      onResolved();
      return response.resumed;
    }, onFrame);
  }

  return {
    agent,
    ask,
    loadChat,
    loading,
    model,
    resetChat,
    resolveAction,
    resolveQuestions,
    setAgent,
    setChatId,
    setModel,
    stopTurn: stop,
  };
}
